import { Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, Switch, Text } from "@chakra-ui/react"
import { CheckmarkCircleSolidIcon, EditIcon, MoreHorizontalSolidIcon } from "@iac/components.icons.react"
import { useTranslation } from "@iac/translations.i18n-instance"
import { FC, ReactNode, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Column, Row, usePagination, useTable } from "react-table"

import { TenancyLanguage, api } from "api"
import { Pagination, Table } from "common/components"
import { DEFAULT_ITEMS_PER_PAGE, TENANCY_ID } from "common/constants"
import { route } from "features/app/routes"

type LanguagesTableProps = {
  loading: boolean
  tenancyLanguages: TenancyLanguage[]
  refetch: (top: number, skip: number) => void
  pageCount: number
  itemsPerPage: number
}

const LanguagesTable: FC<LanguagesTableProps> = ({ tenancyLanguages, loading, refetch, pageCount, itemsPerPage }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [id, setId] = useState("")
  const updateTenancyLanguage = api.useUpdateTenancyLanguageMutation(
    { onSuccess: () => navigate(route.languages.path) },
    TENANCY_ID,
    id
  )

  const setDefaultLanguage = async (language: TenancyLanguage) => {
    await setId(language.languageId)
    await update()
  }

  const update = () => {
    const object = {
      isDefault: true,
    }
    updateTenancyLanguage.mutate(object)
  }

  const NameCell: FC<{ language: TenancyLanguage }> = ({ language }) => (
    <Flex align="center">
      <Text mr={6} width="70%">
        {language.language.name}
      </Text>
      <Menu placement="left">
        <MenuButton
          aria-label="Options"
          as={IconButton}
          background="ui.01"
          borderColor="ui.02"
          borderWidth={1}
          icon={<MoreHorizontalSolidIcon color="black" />}
          onClick={() => setId(language.languageId)}
        />
        <MenuList>
          {language.isDefault ? (
            <>
              <MenuItem icon={<CheckmarkCircleSolidIcon color="success.01" />}>
                <Text fontSize={16} ml={6}>
                  {t("default")}
                </Text>
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem icon={<EditIcon color="ui.04" fontSize={20} />} onClick={() => navigate(language.languageId)}>
                <Text fontSize={16} ml={6}>
                  {t("edit")}
                </Text>
              </MenuItem>
              <MenuItem
                icon={<CheckmarkCircleSolidIcon color="success.01" />}
                onClick={() => setDefaultLanguage(language)}
              >
                <Text fontSize={16} ml={6}>
                  {t("languages.default")}
                </Text>
              </MenuItem>
            </>
          )}
        </MenuList>
      </Menu>
    </Flex>
  )

  const columnData: Column<TenancyLanguage>[] = [
    {
      Header: t("languages.language") as string,
      // @ts-ignore
      Cell: ({ row }) => <NameCell language={row.original} />,
    },
    {
      Header: t("languages.enabled") as string,
      // @ts-ignore
      Cell: ({ row }) => <Switch isChecked={row.original.isEnabled} readOnly />,
    },
    {
      Header: t("languages.systemLanguage") as string,
      // @ts-ignore
      Cell: ({ row }) => <Switch isChecked={row.original.isSystem} readOnly />,
    },
    {
      Header: t("languages.defaultLanguage") as string,
      accessor: "isDefault",
      // @ts-ignore
      Cell: ({ row }) => <Switch isChecked={row.original.isDefault} readOnly />,
    },
  ]

  const {
    getTableProps,
    getTableBodyProps,
    setPageSize,
    headerGroups,
    prepareRow,
    page,
    pageOptions,
    gotoPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      columns: useMemo(() => columnData, [tenancyLanguages]),
      data: useMemo(() => tenancyLanguages, [tenancyLanguages]),
      initialState: {
        pageSize: itemsPerPage,
      },
      manualPagination: true,
      pageCount,
    },
    usePagination
  )

  /* eslint-disable react/jsx-key */
  const renderRow = (row: Row<TenancyLanguage>): ReactNode => {
    prepareRow(row)

    return (
      <Table.TR {...row.getRowProps()}>
        {row.cells.map((cell) => (
          <Table.TD _notFirst={{ textAlign: "center" }} {...cell.getCellProps()}>
            {cell.render("Cell")}
          </Table.TD>
        ))}
      </Table.TR>
    )
  }

  return (
    <Table {...getTableProps()} data-testid="languagesTable" width="100%">
      <Table.Header>
        {headerGroups.map((headerGroup) => (
          <Table.TR bg="white" color="#36495E" fontSize="lg" {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <Table.TH _notFirst={{ textAlign: "center" }} {...column.getHeaderProps()} textTransform="capitalize">
                {column.render("Header")}
              </Table.TH>
            ))}
          </Table.TR>
        ))}
      </Table.Header>
      <Table.Body {...getTableBodyProps()} textAlign="left">
        {loading ? (
          <Table.Skeleton columnWidths={[32, 16, 32, 64]} rowCount={DEFAULT_ITEMS_PER_PAGE} />
        ) : (
          <>{page.map(renderRow)}</>
        )}
      </Table.Body>
      <Table.Footer>
        <Table.TR bg="#fafafa">
          <Table.TD colSpan={columnData.length}>
            <Pagination
              currentPage={pageIndex}
              pageSize={pageSize}
              setPageSize={(event) => {
                setPageSize(event)
                refetch(event, 0)
              }}
              totalPages={pageOptions.length}
              onPageChange={(pageIndex) => {
                gotoPage(pageIndex)
                refetch(pageSize, pageIndex * pageSize)
              }}
            />
          </Table.TD>
        </Table.TR>
      </Table.Footer>
    </Table>
  )
  /* eslint-enable react/jsx-key */
}

export { LanguagesTable }
